/**
 * React Query hooks for Feature Announcements
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as featureService from '@/services/feature-announcements.service';
import { useAuthStore } from '@/stores/authStore';

export const featureKeys = {
  all: ['feature-announcements'] as const,
  list: () => [...featureKeys.all, 'list'] as const,
  dismissed: (userId: string) => [...featureKeys.all, 'dismissed', userId] as const,
  undismissed: (userId: string) => [...featureKeys.all, 'undismissed', userId] as const,
};

// Re-export types
export type {
  FeatureAnnouncement,
  FeatureAnnouncementInsert,
  FeatureAnnouncementUpdate,
} from '@/services/feature-announcements.service';

/**
 * Fetch all active feature announcements
 */
export function useFeatureAnnouncements() {
  return useQuery({
    queryKey: featureKeys.list(),
    queryFn: featureService.getFeatureAnnouncements,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch all feature announcements including inactive (admin only)
 */
export function useAllFeatureAnnouncementsForAdmin() {
  return useQuery({
    queryKey: [...featureKeys.list(), 'admin'] as const,
    queryFn: featureService.getAllFeatureAnnouncementsForAdmin,
    staleTime: 30 * 1000,
  });
}

/**
 * Create a feature announcement (admin)
 */
export function useCreateFeatureAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      input,
      createdByUserId,
      createdByName,
    }: {
      input: featureService.FeatureAnnouncementInsert;
      createdByUserId?: string;
      createdByName?: string;
    }) => featureService.createFeatureAnnouncement(input, createdByUserId, createdByName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.list() });
    },
  });
}

/**
 * Update a feature announcement (admin)
 */
export function useUpdateFeatureAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: featureService.FeatureAnnouncementUpdate }) =>
      featureService.updateFeatureAnnouncement(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.list() });
    },
  });
}

/**
 * Set feature announcement active/inactive (admin)
 */
export function useSetFeatureAnnouncementActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      featureService.setFeatureAnnouncementActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.list() });
    },
  });
}

/**
 * Fetch undismissed features for the current user (for notifications)
 */
export function useUndismissedFeatures() {
  const { user } = useAuthStore();
  const userId = user?.id;

  return useQuery({
    queryKey: featureKeys.undismissed(userId || ''),
    queryFn: () => featureService.getUndismissedFeatures(userId!),
    enabled: !!userId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Dismiss a single feature
 */
export function useDismissFeature() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: (featureId: string) => {
      if (!user?.id) throw new Error('Not authenticated');
      return featureService.dismissFeature(user.id, featureId);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: featureKeys.undismissed(user.id) });
        queryClient.invalidateQueries({ queryKey: featureKeys.dismissed(user.id) });
      }
    },
  });
}

/**
 * Dismiss all features
 */
export function useDismissAllFeatures() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: (featureIds: string[]) => {
      if (!user?.id) throw new Error('Not authenticated');
      return featureService.dismissAllFeatures(user.id, featureIds);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: featureKeys.undismissed(user.id) });
        queryClient.invalidateQueries({ queryKey: featureKeys.dismissed(user.id) });
      }
    },
  });
}
